import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Badge, Alert, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faPlus, 
  faEdit, 
  faTrash, 
  faEye,
  faSearch,
  faArrowLeft,
  faBuilding,
  faUserShield,
  faEnvelope,
  faCalendarAlt,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { userService } from '../../services/userService';
import { companyService } from '../../services/companyService';

const UsersList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersData, companiesData] = await Promise.all([
          userService.getAllUsers(),
          companyService.getAllCompanies()
        ]);
        setUsers(usersData);
        setCompanies(companiesData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to fetch users data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await userService.deleteUser(userId);
        setUsers(users.filter(user => user.id !== userId));
      } catch (error) {
        setError('Failed to delete user');
        console.error('Error deleting user:', error);
      }
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'N/A';
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge bg="danger">Admin</Badge>;
      case 'manager':
        return <Badge bg="info">Manager</Badge>;
      case 'tenant':
        return <Badge bg="warning">Tenant</Badge>;
      default:
        return <Badge bg="secondary">{role}</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    return status === 'active' ? 
      <Badge bg="success">Active</Badge> : 
      <Badge bg="secondary">Inactive</Badge>;
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">System Users</h2>
          <p className="text-muted mb-0">Manage all users in the system</p>
        </div>
        <div className="d-flex gap-2">
          <Button as="a" href="/admin/users/create" variant="primary">
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add New User
          </Button>
          <Button variant="outline-secondary" onClick={() => navigate('/dashboard')}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-primary bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faUsers} className="text-primary" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{users.length}</h4>
                  <p className="text-muted mb-0">Total Users</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-danger bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faUserShield} className="text-danger" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{users.filter(u => u.role === 'admin').length}</h4>
                  <p className="text-muted mb-0">System Admins</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-info bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faBuilding} className="text-info" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{users.filter(u => u.role === 'manager').length}</h4>
                  <p className="text-muted mb-0">Property Managers</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-warning bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faUser} className="text-warning" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{users.filter(u => u.role === 'tenant').length}</h4>
                  <p className="text-muted mb-0">Tenants</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-success bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faEnvelope} className="text-success" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{users.filter(u => u.status === 'active').length}</h4>
                  <p className="text-muted mb-0">Active Users</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
              >
                <option value="all">All Roles</option>
                <option value="admin">Admins</option>
                <option value="manager">Managers</option>
                <option value="tenant">Tenants</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <div className="text-muted">
                {filteredUsers.length} of {users.length}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Users Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-transparent border-0">
          <h5 className="mb-0">Users List</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="bg-light">
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Company</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" 
                             style={{ width: '40px', height: '40px' }}>
                          <FontAwesomeIcon icon={faUsers} className="text-primary" />
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <div className="fw-bold">{user.name}</div>
                        <small className="text-muted">{user.email}</small>
                      </div>
                    </div>
                  </td>
                  <td>{getRoleBadge(user.role)}</td>
                  <td>
                    {user.role === 'manager' ? 
                      getCompanyName(user.companyId) : 
                      <span className="text-muted">System Admin</span>
                    }
                  </td>
                  <td>{getStatusBadge(user.status)}</td>
                  <td>
                    {user.createdAt ? 
                      user.createdAt.toDate().toLocaleDateString() : 
                      'N/A'
                    }
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                        title="View Details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                        title="Edit User"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteUser(user.id)}
                        title="Delete User"
                        disabled={user.role === 'admin'} // Prevent deleting admins
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {filteredUsers.length === 0 && (
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faUsers} className="text-muted mb-3" size="3x" />
              <h5 className="text-muted">No users found</h5>
              <p className="text-muted">
                {searchTerm || filterRole !== 'all' || filterStatus !== 'all'
                  ? 'Try adjusting your search or filters' 
                  : 'Get started by adding your first user'
                }
              </p>
              {!searchTerm && filterRole === 'all' && filterStatus === 'all' && (
                <Button as="a" href="/admin/users/create" variant="primary">
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add First User
                </Button>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default UsersList; 